import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Button,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SavingsIcon from "@mui/icons-material/Savings";
import InputIcon from "@mui/icons-material/Input";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { getNeedlepathMetricsSummary, listNeedlepathMetricRuns } from "../api/agentApi";
import { useTheme } from "@mui/material/styles";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";

const COPPER = BRAND_COLORS.primary.main;
const COPPER_DARK = BRAND_COLORS.primary.dark;
const ORCA = "var(--needle-ink-strong)";
const INK = "var(--needle-ink)";
const MUTED = "var(--needle-muted)";
const PANEL = "var(--needle-panel)";
const BORDER = "var(--needle-border)";
const SOFT = "var(--needle-panel-soft)";
const SUCCESS = "#789769";
const STEEL = "#5A7D9A";
const FAIL = "#B45F5F";
const ACTION_BUTTON_SX = {
  minHeight: 44,
  borderRadius: UI_RADIUS.button,
  px: 2.35,
  bgcolor: "primary.main",
  color: "primary.contrastText",
  textTransform: "none",
  fontWeight: 850,
  letterSpacing: 0,
  boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
  transition: "transform 160ms ease, background-color 160ms ease, box-shadow 160ms ease",
  "& .MuiButton-startIcon": {
    color: "inherit",
    mr: 1,
  },
  "&:hover": {
    bgcolor: "primary.dark",
    boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}`,
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    bgcolor: "#D8C8B9",
    color: "rgba(255,255,255,0.88)",
    boxShadow: "none",
    transform: "none",
  },
};
const DASHBOARD_CARD_EFFECT = {
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow:
    "0 2px 8px -2px rgba(0,0,0,0.1), 0 6px 12px -2px rgba(0,0,0,0.06)",
  "&:hover": {
    borderColor: COPPER,
    boxShadow:
      "0 4px 12px -2px rgba(0,0,0,0.12), 0 12px 24px -4px rgba(0,0,0,0.08)",
    transform: "translateY(-2px)",
  },
};
const CATEGORY_ORDER = [
  "agent_execution",
  "agent_direct",
  "agent_task_lock",
  "agent_enhancer",
  "knowledge_rag",
  "tool_generation",
  "tool_enhancer",
];
const CATEGORY_LABELS = {
  agent_execution: "Execution",
  agent_direct: "Planning",
  agent_task_lock: "Task Framing",
  agent_enhancer: "Agent Enhancer",
  knowledge_rag: "Knowledge / RAG",
  tool_generation: "Tool Generation",
  tool_enhancer: "Tool Enhancer",
};
const AGENT_CATEGORY_IDS = new Set([
  "agent_execution",
  "agent_direct",
  "agent_task_lock",
  "agent_enhancer",
]);

function pct(value) {
  return `${Math.round((Number(value) || 0) * 1000) / 10}%`;
}

function compactNumber(value) {
  return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(Number(value) || 0);
}

function fullNumber(value) {
  return new Intl.NumberFormat("en").format(Number(value) || 0);
}

function stageLabel(value) {
  return String(value || "unknown")
    .replace(/^rag:docrag:/, "RAG ")
    .replace(/_/g, " ")
    .replace(/:/g, " / ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function categoryForStage(value) {
  const raw = String(value || "unknown");
  const stage = raw.toLowerCase();
  let id = `other:${raw}`;

  if (stage === "agent:exec" || stage === "agent:system") {
    id = "agent_execution";
  } else if (stage === "agent:direct") {
    id = "agent_direct";
  } else if (stage === "agent:task_lock") {
    id = "agent_task_lock";
  } else if (stage.startsWith("enhancer:") || stage.startsWith("intent_to_agent:")) {
    id = "agent_enhancer";
  } else if (stage.startsWith("rag:")) {
    id = "knowledge_rag";
  } else if (stage.startsWith("toolforge:")) {
    const toolStage = stage.replace(/^toolforge:/, "");
    id = toolStage.includes("enhance") || toolStage.includes("enhancer") || toolStage.includes("assistant")
      ? "tool_enhancer"
      : "tool_generation";
  }

  return {
    id,
    label: CATEGORY_LABELS[id] || stageLabel(raw),
    order: CATEGORY_ORDER.includes(id) ? CATEGORY_ORDER.indexOf(id) : CATEGORY_ORDER.length,
  };
}

function sortByCategory(left, right) {
  if (left.order !== right.order) {
    return left.order - right.order;
  }
  return left.label.localeCompare(right.label);
}

function metricTone(value) {
  const number = Number(value) || 0;
  return number > 0 ? COPPER : "#C3BBB3";
}

function MetricTile({ label, value, caption, icon, accent = COPPER }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        py: 1.75,
        px: 2,
        borderRight: { xs: "none", md: "1px solid rgba(232,216,202,0.9)" },
        "&:last-of-type": { borderRight: "none" },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: UI_RADIUS.panel,
            display: "grid",
            placeItems: "center",
            bgcolor: `${accent}18`,
            color: accent,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{ color: MUTED, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0 }}
        >
          {label}
        </Typography>
      </Stack>
      <Typography variant="h3" sx={{ color: ORCA, fontWeight: 850, lineHeight: 1, fontSize: { xs: 34, md: 42 } }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="body2" sx={{ color: MUTED, mt: 0.75, overflow: "hidden", textOverflow: "ellipsis" }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
}

function HeaderSavingsBadge({ value }) {
  const numericValue = Number(value) || 0;
  const hasSavings = numericValue > 0;
  const accent = hasSavings ? SUCCESS : MUTED;
  const textColor = hasSavings ? SUCCESS : MUTED;

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "inline-flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.5,
        py: 0.85,
        borderRadius: UI_RADIUS.panel,
        border: `1px solid ${hasSavings ? "rgba(120,151,105,0.34)" : "rgba(232,216,202,0.9)"}`,
        bgcolor: hasSavings ? "rgba(120,151,105,0.14)" : SOFT,
        backgroundImage: "none",
        boxShadow: hasSavings
          ? "0 2px 8px -3px rgba(63,105,56,0.28), 0 10px 22px -12px rgba(63,105,56,0.28)"
          : "0 2px 8px -5px rgba(11,11,11,0.18)",
        minHeight: 42,
        transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(115deg, transparent 0%, transparent 34%, rgba(255,255,255,0.16) 48%, transparent 62%, transparent 100%)",
          transform: "translateX(-120%)",
          transition: "transform 620ms ease",
          pointerEvents: "none",
        },
        "&:hover": {
          borderColor: hasSavings ? "rgba(120,151,105,0.58)" : BORDER,
          boxShadow: hasSavings
            ? "0 4px 12px -4px rgba(63,105,56,0.34), 0 16px 30px -16px rgba(63,105,56,0.34)"
            : "0 4px 12px -6px rgba(11,11,11,0.18)",
          transform: "translateY(-1px)",
        },
        "&:hover::before": {
          transform: "translateX(120%)",
        },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: UI_RADIUS.panel,
          display: "grid",
          placeItems: "center",
          bgcolor: hasSavings ? "rgba(120,151,105,0.16)" : SOFT,
          color: accent,
          flexShrink: 0,
        }}
      >
        <SavingsIcon sx={{ fontSize: 17 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: textColor, fontWeight: 950, lineHeight: 1, fontSize: 20 }}>
          {compactNumber(numericValue)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: textColor, display: "block", fontWeight: 800, lineHeight: 1.1, opacity: 0.82 }}
        >
          total tokens saved
        </Typography>
      </Box>
    </Box>
  );
}

function SectionPanel({ title, subtitle, children, action }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        border: `1px solid ${BORDER}`,
        borderRadius: UI_RADIUS.panel,
        bgcolor: PANEL,
        minHeight: "100%",
        ...DASHBOARD_CARD_EFFECT,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ color: INK, fontWeight: 800, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function EmptyState({ title, body }) {
  return (
    <Box
      sx={{
        minHeight: 180,
        border: `1px dashed ${BORDER}`,
        borderRadius: UI_RADIUS.panel,
        bgcolor: SOFT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 2,
      }}
    >
      <Box>
        <AutoGraphIcon sx={{ color: "#C8B6A4", fontSize: 34, mb: 1 }} />
        <Typography sx={{ fontWeight: 800, color: INK }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: MUTED, mt: 0.5, maxWidth: 360 }}>
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

export default function NeedlepathDashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [hours, setHours] = useState(24);
  const [summary, setSummary] = useState(null);
  const [runs, setRuns] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryData, runsData] = await Promise.all([
        getNeedlepathMetricsSummary(hours),
        listNeedlepathMetricRuns(80),
      ]);
      const nextRuns = runsData?.runs || [];
      setSummary(summaryData);
      setRuns(nextRuns);
      setExpandedStages((current) => {
        if (Object.keys(current).length > 0) {
          return current;
        }
        const firstCategory = nextRuns[0]?.stage ? categoryForStage(nextRuns[0].stage).id : "";
        return firstCategory ? { [firstCategory]: true } : {};
      });
    } catch (err) {
      console.error(err);
      setError("Could not load Needlepath metrics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hours]);

  const excludedIngestionRuns = Number(summary?.excluded_ingestion_runs) || 0;
  const stageRows = Object.values(
    (summary?.by_stage || []).reduce((groups, stage) => {
      const category = categoryForStage(stage.stage);
      if (!groups[category.id]) {
        groups[category.id] = {
          id: category.id,
          label: category.label,
          order: category.order,
          tokens_saved: 0,
          rawStages: new Set(),
          total_runs: 0,
          input_tokens_before: 0,
          input_tokens_after: 0,
          output_tokens: 0,
        };
      }
      groups[category.id].total_runs += Number(stage.total_runs) || 0;
      groups[category.id].input_tokens_before += Number(stage.input_tokens_before) || 0;
      groups[category.id].input_tokens_after += Number(stage.input_tokens_after) || 0;
      groups[category.id].output_tokens += Number(stage.output_tokens) || 0;
      groups[category.id].tokens_saved += Number(stage.tokens_saved) || 0;
      groups[category.id].rawStages.add(stage.stage || "unknown");
      return groups;
    }, {})
  ).sort(sortByCategory);
  const agentStageTotals = stageRows
    .filter((stage) => AGENT_CATEGORY_IDS.has(stage.id))
    .reduce(
      (totals, stage) => ({
        total_runs: totals.total_runs + (Number(stage.total_runs) || 0),
        input_tokens_before: totals.input_tokens_before + (Number(stage.input_tokens_before) || 0),
        input_tokens_after: totals.input_tokens_after + (Number(stage.input_tokens_after) || 0),
        output_tokens: totals.output_tokens + (Number(stage.output_tokens) || 0),
        tokens_saved: totals.tokens_saved + (Number(stage.tokens_saved) || 0),
      }),
      {
        total_runs: 0,
        input_tokens_before: 0,
        input_tokens_after: 0,
        output_tokens: 0,
        tokens_saved: 0,
      }
    );
  const backendAgentTotals = summary?.agent_totals || {};
  const backendAgentRunCount = Number(backendAgentTotals.total_runs) || 0;
  const useBackendAgentTotals = backendAgentRunCount > 0 || agentStageTotals.total_runs === 0;
  const agentTotals = useBackendAgentTotals
    ? backendAgentTotals
    : {
        ...agentStageTotals,
        applied_runs: 0,
        fallback_runs: 0,
        avg_reduction_ratio: agentStageTotals.input_tokens_before
          ? agentStageTotals.tokens_saved / agentStageTotals.input_tokens_before
          : 0,
      };
  const appliedRuns = Number(agentTotals.applied_runs) || 0;
  const totalRuns = Number(agentTotals.total_runs) || 0;
  const passiveRuns = Math.max(totalRuns - appliedRuns, 0);
  const fallbackRuns = Number(agentTotals.fallback_runs) || 0;
  const savedTokens = Number(agentTotals.tokens_saved) || 0;
  const inputBefore = Number(agentTotals.input_tokens_before) || 0;
  const inputAfter = Number(agentTotals.input_tokens_after) || 0;
  const outputTokens = Number(agentTotals.output_tokens) || 0;
  const hasAgentData = totalRuns > 0;
  const stageSavedTotal = stageRows.reduce((total, stage) => total + (Number(stage.tokens_saved) || 0), 0);
  const runCollections = Object.values(
    runs.reduce((groups, run) => {
      const category = categoryForStage(run.stage);
      if (!groups[category.id]) {
        groups[category.id] = {
          id: category.id,
          label: category.label,
          order: category.order,
          rawStages: new Set(),
          runs: [],
          applied: 0,
          inputBefore: 0,
          inputAfter: 0,
          tokensSaved: 0,
          outputTokens: 0,
          latestAt: run.created_at || "",
        };
      }
      groups[category.id].runs.push(run);
      groups[category.id].rawStages.add(run.stage || "unknown");
      groups[category.id].applied += run.applied ? 1 : 0;
      groups[category.id].inputBefore += Number(run.input_tokens_before) || 0;
      groups[category.id].inputAfter += Number(run.input_tokens_after) || 0;
      groups[category.id].tokensSaved += Number(run.tokens_saved) || 0;
      groups[category.id].outputTokens += Number(run.output_tokens) || 0;
      if (run.created_at && (!groups[category.id].latestAt || new Date(run.created_at) > new Date(groups[category.id].latestAt))) {
        groups[category.id].latestAt = run.created_at;
      }
      return groups;
    }, {})
  ).sort(sortByCategory);

  const toggleStage = (stage) => {
    setExpandedStages((current) => ({ ...current, [stage]: !current[stage] }));
  };

  return (
    <Box
      sx={{
        pl: PAGE_SHELL.paddingLeft,
        pr: PAGE_SHELL.paddingRight,
        py: PAGE_SHELL.paddingY,
        maxWidth: PAGE_SHELL.maxWidth,
        mx: "auto",
        color: "text.primary",
        "--needle-canvas": isDark ? "#0F0D0B" : "#FBFAF8",
        "--needle-panel": isDark ? "#181411" : "#FFFFFF",
        "--needle-panel-soft": isDark ? "#211B16" : "#F5EEE8",
        "--needle-ink-strong": isDark ? "#FFF7EF" : "#0B0B0B",
        "--needle-ink": isDark ? "#FFF7EF" : "#1D1D1F",
        "--needle-muted": isDark ? "#D9C9BA" : "#6F6A65",
        "--needle-border": isDark ? "rgba(224,177,132,0.24)" : "#E8D8CA",
      }}
    >
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2.5, flexWrap: { xs: "wrap", md: "nowrap" } }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
              Needlepath
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.35 }}>
              Track prompt optimization, token savings, and eligible model-call decisions.
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }} spacing={1.25} flexWrap="wrap" sx={{ flexShrink: 0, pt: { xs: 0, md: 0.5 } }}>
            <HeaderSavingsBadge value={stageSavedTotal} />
            <Chip
              size="small"
              label={loading ? "Syncing" : "Live"}
              sx={{ bgcolor: loading ? SOFT : "rgba(120,151,105,0.16)", color: loading ? MUTED : SUCCESS, borderRadius: UI_RADIUS.chip, fontWeight: 800 }}
            />
            <Chip
              size="small"
              label="BETA"
              sx={{
                bgcolor: SOFT,
                color: COPPER_DARK,
                border: `1px solid ${BRAND_COLORS.primary.border}`,
                borderRadius: UI_RADIUS.chip,
                fontWeight: 900,
                letterSpacing: 0.2,
              }}
            />
          </Stack>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
          <TextField
            select
            label="Window"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            size="small"
            sx={{
              minWidth: 142,
              "& .MuiOutlinedInput-root": {
                bgcolor: SOFT,
                borderRadius: UI_RADIUS.input,
                "& fieldset": { borderColor: BORDER },
                "&:hover fieldset": { borderColor: BRAND_COLORS.primary.border },
              },
            }}
          >
            <MenuItem value={1}>1 hour</MenuItem>
            <MenuItem value={24}>24 hours</MenuItem>
            <MenuItem value={168}>7 days</MenuItem>
            <MenuItem value={720}>30 days</MenuItem>
          </TextField>
          <Tooltip title={loading ? "Refreshing" : "Refresh metrics"}>
            <span>
              <Button
                variant="contained"
                disableElevation
                aria-label="Refresh Needlepath metrics"
                onClick={load}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <RefreshIcon fontSize="small" />}
                sx={ACTION_BUTTON_SX}
              >
                Refresh
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mt: PAGE_SHELL.sectionGap }}>{error}</Alert>}
      {summary && summary.available === false && (
        <Alert severity="warning" sx={{ mt: PAGE_SHELL.sectionGap }}>
          Metrics table is not available yet. Run migrations before expecting live Needlepath data.
        </Alert>
      )}

      {summary && (
        <>
          <Paper
            elevation={0}
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: UI_RADIUS.panel,
              bgcolor: PANEL,
              overflow: "hidden",
              mb: PAGE_SHELL.sectionGap,
              ...DASHBOARD_CARD_EFFECT,
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 2.5 },
                py: 1.5,
                bgcolor: SOFT,
                borderBottom: `1px solid ${BORDER}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                <ShieldOutlinedIcon sx={{ color: COPPER, fontSize: 19 }} />
                <Typography variant="body2" sx={{ color: INK, fontWeight: 800 }}>
                  Agent Optimizations
                </Typography>
                <Typography variant="body2" sx={{ color: MUTED }}>
                  {hasAgentData ? `${compactNumber(totalRuns)} captured` : "No agent runs"}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                {excludedIngestionRuns > 0 && (
                  <Chip
                    size="small"
                    label={`${compactNumber(excludedIngestionRuns)} ingestion excluded`}
                    sx={{ bgcolor: "rgba(90,125,154,0.18)", color: STEEL, borderRadius: UI_RADIUS.chip, fontWeight: 800 }}
                  />
                )}
                <Tooltip title="Document-ingestion extractor calls are excluded from this runtime view.">
                  <IconButton size="small" sx={{ color: MUTED, borderRadius: UI_RADIUS.iconButton }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                },
              }}
            >
              <MetricTile
                label="Observed"
                value={compactNumber(totalRuns)}
                caption={`${compactNumber(appliedRuns)} active, ${compactNumber(passiveRuns)} passive`}
                icon={<AutoGraphIcon fontSize="small" />}
                accent={STEEL}
              />
              <MetricTile
                label="Input saved"
                value={compactNumber(savedTokens)}
                caption={`${pct(agentTotals.avg_reduction_ratio)} reduction`}
                icon={<SavingsIcon fontSize="small" />}
                accent={metricTone(savedTokens)}
              />
              <MetricTile
                label="Input after"
                value={compactNumber(inputAfter)}
                caption={`from ${compactNumber(inputBefore)}`}
                icon={<InputIcon fontSize="small" />}
                accent={SUCCESS}
              />
              <MetricTile
                label="Output"
                value={compactNumber(outputTokens)}
                caption={`${compactNumber(fallbackRuns)} fail-open`}
                icon={<ChatBubbleOutlineIcon fontSize="small" />}
                accent={fallbackRuns > 0 ? FAIL : "#8C8177"}
              />
            </Box>
          </Paper>

          <SectionPanel
            title="Optimization Activity"
            subtitle="Where Needlepath saved prompt tokens and how eligible model calls were handled."
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.45fr) minmax(300px, 0.85fr)" },
                gap: { xs: 3, lg: 0 },
              }}
            >
              <Box sx={{ pr: { lg: 3 }, borderRight: { lg: `1px solid ${BORDER}` } }}>
                <Typography
                  variant="overline"
                  sx={{ color: MUTED, fontWeight: 900, letterSpacing: 0, textTransform: "uppercase" }}
                >
                  Stage Savings
                </Typography>
                {stageRows.length === 0 ? (
                  <Box sx={{ py: 2.5 }}>
                    <Typography sx={{ color: INK, fontWeight: 850 }}>No stage savings yet</Typography>
                    <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
                      Eligible model calls will appear here once Needlepath removes prompt tokens.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={0.1} sx={{ mt: 0.75 }}>
                    {stageRows.map((stage) => {
                      const saved = Number(stage.tokens_saved) || 0;
                      const totalTokens = Number(stage.input_tokens_after) || 0;
                      const beforeTokens = Number(stage.input_tokens_before) || 0;
                      const share = stageSavedTotal > 0 ? Math.round((saved / stageSavedTotal) * 100) : 0;
                      return (
                        <Box
                          key={stage.id}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "minmax(0, 1fr) auto",
                            gap: 2,
                            alignItems: "center",
                            py: 1.35,
                            borderBottom: `1px solid ${BORDER}`,
                            "&:last-of-type": { borderBottom: "none" },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={1.25}>
                              <Box
                                sx={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: "50%",
                                  bgcolor: saved > 0 ? COPPER : "#CEC6BE",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                sx={{
                                  color: INK,
                                  fontWeight: 850,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {stage.label}
                              </Typography>
                              <Chip
                                size="small"
                                label={`${compactNumber(totalTokens)} total tokens`}
                                sx={{
                                  height: 22,
                                  bgcolor: SOFT,
                                  color: MUTED,
                                  borderRadius: UI_RADIUS.chip,
                                  fontWeight: 800,
                                  maxWidth: "100%",
                                  "& .MuiChip-label": {
                                    px: 0.9,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  },
                                }}
                              />
                            </Stack>
                            <Typography variant="caption" sx={{ color: MUTED, ml: 2.75 }}>
                              {saved > 0
                                ? `${share}% of saved prompt tokens · ${compactNumber(beforeTokens)} before optimization`
                                : `No savings recorded${stage.rawStages?.size ? ` across ${stage.rawStages.size} source stage${stage.rawStages.size === 1 ? "" : "s"}` : ""}`}
                            </Typography>
                          </Box>
                          <Stack alignItems="flex-end" sx={{ minWidth: 96 }}>
                            <Typography
                              sx={{
                                color: saved > 0 ? COPPER_DARK : MUTED,
                                fontWeight: 900,
                                lineHeight: 1,
                                fontSize: 22,
                              }}
                            >
                              {compactNumber(saved)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: MUTED, whiteSpace: "nowrap" }}>
                              saved
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>

              <Box sx={{ pl: { lg: 3 } }}>
                <Typography
                  variant="overline"
                  sx={{ color: MUTED, fontWeight: 900, letterSpacing: 0, textTransform: "uppercase" }}
                >
                  Traffic Mix
                </Typography>
                <Stack spacing={0.1} sx={{ mt: 0.75 }}>
                  {[
                    ["Applied", appliedRuns, COPPER_DARK, totalRuns ? `${Math.round((appliedRuns / totalRuns) * 100)}% active optimization` : "No applied calls"],
                    ["Observed", passiveRuns, MUTED, totalRuns ? `${Math.round((passiveRuns / totalRuns) * 100)}% measured only` : "No observed-only calls"],
                    ["Fail-open", fallbackRuns, fallbackRuns > 0 ? FAIL : MUTED, "Safety fallback events"],
                  ].map(([label, value, valueColor, helper]) => (
                    <Box
                      key={label}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        alignItems: "center",
                        gap: 2,
                        py: 1.35,
                        borderBottom: `1px solid ${BORDER}`,
                        "&:last-of-type": { borderBottom: "none" },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: INK, fontWeight: 850 }}>{label}</Typography>
                        <Typography variant="caption" sx={{ color: MUTED }}>{helper}</Typography>
                      </Box>
                      <Typography sx={{ color: valueColor, fontWeight: 900, fontSize: 24, lineHeight: 1 }}>
                        {fullNumber(value)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          </SectionPanel>

          <Paper
            elevation={0}
            sx={{
              mt: PAGE_SHELL.sectionGap,
              p: { xs: 2, md: 2.5 },
              border: `1px solid ${BORDER}`,
              borderRadius: UI_RADIUS.panel,
              bgcolor: PANEL,
              ...DASHBOARD_CARD_EFFECT,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ color: INK, fontWeight: 800 }}>
                  Recent Model Calls
                </Typography>
                <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
                  Latest Needlepath decisions for eligible agent activity.
                </Typography>
              </Box>
              {runCollections.length > 0 && (
                <Chip
                  size="small"
                  label={`${compactNumber(runCollections.length)} categories`}
                  sx={{ bgcolor: SOFT, color: COPPER_DARK, borderRadius: UI_RADIUS.chip, fontWeight: 800 }}
                />
              )}
            </Stack>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { color: MUTED, fontWeight: 850, borderBottomColor: BORDER } }}>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Calls</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Before</TableCell>
                    <TableCell align="right">After</TableCell>
                    <TableCell align="right">Saved</TableCell>
                    <TableCell align="right">Output</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runCollections.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ borderBottom: "none", pt: 2 }}>
                        <EmptyState
                          title="No model calls captured"
                          body="Your next eligible agent run will populate this table automatically."
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    runCollections.map((collection) => {
                      const isExpanded = Boolean(expandedStages[collection.id]);
                      const observed = collection.runs.length - collection.applied;
                      return (
                        <React.Fragment key={collection.id}>
                          <TableRow
                            hover
                            onClick={() => toggleStage(collection.id)}
                            sx={{
                              cursor: "pointer",
                              bgcolor: isExpanded ? SOFT : PANEL,
                              "& td": { borderBottomColor: BORDER, py: 1.35 },
                            }}
                          >
                            <TableCell sx={{ minWidth: 300 }}>
                              <Stack direction="row" alignItems="center" spacing={1.25}>
                                <IconButton
                                  size="small"
                                  aria-label={`${isExpanded ? "Collapse" : "Expand"} ${collection.label}`}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: UI_RADIUS.iconButton,
                                    color: COPPER_DARK,
                                    bgcolor: isExpanded ? SOFT : "transparent",
                                  }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleStage(collection.id);
                                  }}
                                >
                                  {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                </IconButton>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ color: INK, fontWeight: 850 }}>
                                    {collection.label}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: MUTED }}>
                                    {collection.rawStages?.size || 0} source stage{collection.rawStages?.size === 1 ? "" : "s"} · Latest{" "}
                                    {collection.latestAt ? new Date(collection.latestAt).toLocaleString() : "unknown"}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 850 }}>
                              {fullNumber(collection.runs.length)}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                                {collection.applied > 0 && (
                                  <Chip
                                    size="small"
                                    label={`${compactNumber(collection.applied)} applied`}
                                    sx={{ bgcolor: "rgba(120,151,105,0.16)", color: SUCCESS, borderRadius: UI_RADIUS.chip, fontWeight: 800 }}
                                  />
                                )}
                                {observed > 0 && (
                                  <Chip
                                    size="small"
                                    label={`${compactNumber(observed)} observed`}
                                    sx={{ bgcolor: SOFT, color: MUTED, borderRadius: UI_RADIUS.chip, fontWeight: 800 }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell align="right">{compactNumber(collection.inputBefore)}</TableCell>
                            <TableCell align="right">{compactNumber(collection.inputAfter)}</TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: collection.tokensSaved > 0 ? COPPER_DARK : MUTED, fontWeight: 900 }}
                            >
                              {compactNumber(collection.tokensSaved)}
                            </TableCell>
                            <TableCell align="right">{compactNumber(collection.outputTokens)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={7} sx={{ p: 0, borderBottomColor: BORDER }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ px: { xs: 1.5, md: 6.5 }, py: 1.25, bgcolor: SOFT }}>
                                  <Table size="small" aria-label={`${collection.label} model calls`}>
                                    <TableHead>
                                      <TableRow
                                        sx={{
                                          "& th": {
                                            color: MUTED,
                                            fontWeight: 850,
                                            borderBottomColor: BORDER,
                                            fontSize: 12,
                                          },
                                        }}
                                      >
                                        <TableCell>Time</TableCell>
                                        <TableCell>Source Stage</TableCell>
                                        <TableCell>Mode</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Before</TableCell>
                                        <TableCell align="right">After</TableCell>
                                        <TableCell align="right">Saved</TableCell>
                                        <TableCell align="right">Output</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {collection.runs.map((run) => (
                                        <TableRow
                                          key={run.id}
                                          hover
                                          sx={{ "& td": { borderBottomColor: BORDER, py: 1.05 } }}
                                        >
                                          <TableCell sx={{ color: MUTED, whiteSpace: "nowrap" }}>
                                            {run.created_at ? new Date(run.created_at).toLocaleString() : ""}
                                          </TableCell>
                                          <TableCell sx={{ color: INK, fontWeight: 750, minWidth: 180 }}>
                                            {stageLabel(run.stage)}
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              size="small"
                                              label={run.mode || "off"}
                                              sx={{ bgcolor: SOFT, color: COPPER_DARK, borderRadius: UI_RADIUS.chip, fontWeight: 800, minWidth: 64 }}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              size="small"
                                              label={run.applied ? "Applied" : "Observed"}
                                              sx={{
                                                bgcolor: run.applied ? "rgba(120,151,105,0.16)" : SOFT,
                                                color: run.applied ? SUCCESS : MUTED,
                                                borderRadius: UI_RADIUS.chip,
                                                fontWeight: 800,
                                              }}
                                            />
                                          </TableCell>
                                          <TableCell align="right">{compactNumber(run.input_tokens_before)}</TableCell>
                                          <TableCell align="right">{compactNumber(run.input_tokens_after)}</TableCell>
                                          <TableCell
                                            align="right"
                                            sx={{ color: Number(run.tokens_saved) > 0 ? COPPER_DARK : MUTED, fontWeight: 800 }}
                                          >
                                            {compactNumber(run.tokens_saved)}
                                          </TableCell>
                                          <TableCell align="right">{compactNumber(run.output_tokens)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}
